/**
 * ========================
 * DEMO DOMAIN MENU PROVIDER
 * ========================
 *
 * Menu configuration for the Demo domain.
 *
 * This file is OWNED by the Demo domain.
 * Safe for AI agents to generate or modify.
 *
 * RULES:
 * - Do not import UI layout code
 * - Do not mutate state here
 * - Only return declarative menu definitions
 */

import {
  IconLayoutDashboard,
  IconComponents,
  IconApps,
  IconShoppingCart,
  IconFolders,
  IconUsers,
  IconUserCircle,
  IconFolder,
  IconChartBar,
  IconMessage,
  IconCalendar,
  IconReceipt,
  IconHeadset,
  IconSquare,
  IconForms,
  IconDatabase,
  IconSparkles,
  IconBell,
  IconListCheck,
  IconClick,
  IconTable,
  IconEdit,
  IconChecklist,
  IconBriefcase,
  IconChartLine,
  IconChartPie,
  IconChartArea,
  IconTableOptions,
  IconGauge,
  IconChartDots,
  IconAlertCircle,
  IconBrain,
  IconArrowsMove,
  IconPalette,
  IconMoon,
  IconTypography,
  IconLanguage,
  IconAccessible,
  IconLock,
  IconLogin,
  IconUserPlus,
  IconKey,
  IconLockCheck,
  IconDeviceMobile,
  IconDots,
  IconLayoutGrid,
  IconSettings,
  IconListDetails,
} from '@tabler/icons-react';
import { DomainMenuProvider, MenuSection } from '../../layouts/menu/types';

console.log('🔥🔥🔥 [DEMO MODULE] demoMenu.tsx file is being loaded!');

class DemoMenuProvider implements DomainMenuProvider {
  domainId = 'demo';

  initialize() {
    console.log('[DemoMenuProvider] Initializing demo menu provider');
  }

  getMenuConfig() {
    console.log('[DemoMenuProvider] getMenuConfig called - returning 21 sections');
    const sections: MenuSection[] = [
      // 1. Overview Section
      {
        id: 'overview',
        label: 'Overview',
        items: [
          {
            id: 'demo-home',
            label: 'Demo Home',
            path: '/app/demo',
            icon: <IconLayoutDashboard size={16} />,
          },
        ],
      },

      // 2. Dashboard Section
      {
        id: 'dashboard',
        label: 'Dashboard',
        items: [
          {
            id: 'dashboard-widgets',
            label: 'Dashboard Widgets',
            path: '/app/demo/dashboard',
            icon: <IconLayoutGrid size={16} />,
            countSelector: () => 'New',
            badgeColor: 'green',
          },
        ],
      },

      // 3. Applications Section
      {
        id: 'applications',
        label: 'Applications',
        items: [
          {
            id: 'all-apps',
            label: 'All Apps',
            path: '/app/demo/apps',
            icon: <IconLayoutGrid size={16} />,
          },
        ],
      },

      // 4. E-Commerce
      {
        id: 'ecommerce',
        label: 'E-Commerce',
        items: [
          {
            id: 'ecommerce-dashboard',
            label: 'Dashboard',
            path: '/app/demo/apps/ecommerce',
            icon: <IconLayoutDashboard size={16} />,
          },
          {
            id: 'ecommerce-products',
            label: 'Products',
            path: '/app/demo/apps/ecommerce/products',
            icon: <IconShoppingCart size={16} />,
            countSelector: () => 24,
          },
          {
            id: 'ecommerce-orders',
            label: 'Orders',
            path: '/app/demo/apps/ecommerce/orders',
            icon: <IconListCheck size={16} />,
            countSelector: () => 156,
            badgeColor: 'green',
          },
          {
            id: 'ecommerce-customers',
            label: 'Customers',
            path: '/app/demo/apps/ecommerce/customers',
            icon: <IconUsers size={16} />,
            countSelector: () => 89,
          },
        ],
      },

      // 5. Project Management
      {
        id: 'project-management',
        label: 'Project Management',
        items: [
          {
            id: 'pm-dashboard',
            label: 'Dashboard',
            path: '/app/demo/apps/project-management',
            icon: <IconLayoutDashboard size={16} />,
          },
          {
            id: 'pm-projects',
            label: 'Projects',
            path: '/app/demo/apps/project-management/projects',
            icon: <IconFolders size={16} />,
            countSelector: () => 12,
          },
          {
            id: 'pm-board',
            label: 'Board',
            path: '/app/demo/apps/project-management/board',
            icon: <IconListCheck size={16} />,
            countSelector: () => 8,
            badgeColor: 'orange',
          },
          {
            id: 'pm-team',
            label: 'Team',
            path: '/app/demo/apps/project-management/team',
            icon: <IconUsers size={16} />,
            countSelector: () => 24,
          },
        ],
      },

      // 6. CRM
      {
        id: 'crm',
        label: 'CRM',
        items: [
          {
            id: 'crm-dashboard',
            label: 'Dashboard',
            path: '/app/demo/apps/crm',
            icon: <IconLayoutDashboard size={16} />,
          },
          {
            id: 'crm-pipeline',
            label: 'Pipeline',
            path: '/app/demo/apps/crm/pipeline',
            icon: <IconListCheck size={16} />,
            countSelector: () => '$2.4M',
            badgeColor: 'green',
          },
          {
            id: 'crm-contacts',
            label: 'Contacts',
            path: '/app/demo/apps/crm/contacts',
            icon: <IconUsers size={16} />,
            countSelector: () => 156,
          },
          {
            id: 'crm-activities',
            label: 'Activities',
            path: '/app/demo/apps/crm/activities',
            icon: <IconChecklist size={16} />,
            countSelector: () => 23,
            badgeColor: 'orange',
          },
        ],
      },

      // 7. HR Portal
      {
        id: 'hr-portal',
        label: 'HR Portal',
        items: [
          {
            id: 'hr-dashboard',
            label: 'Dashboard',
            path: '/app/demo/apps/hr-portal',
            icon: <IconLayoutDashboard size={16} />,
          },
          {
            id: 'hr-directory',
            label: 'Directory',
            path: '/app/demo/apps/hr-portal/directory',
            icon: <IconUsers size={16} />,
            countSelector: () => 248,
          },
          {
            id: 'hr-organization',
            label: 'Organization',
            path: '/app/demo/apps/hr-portal/organization',
            icon: <IconFolders size={16} />,
            countSelector: () => 12,
          },
          {
            id: 'hr-leave',
            label: 'Leave Management',
            path: '/app/demo/apps/hr-portal/leave',
            icon: <IconCalendar size={16} />,
            countSelector: () => 5,
            badgeColor: 'red',
          },
        ],
      },

      // 8. File Manager
      {
        id: 'file-manager',
        label: 'File Manager',
        items: [
          {
            id: 'fm-dashboard',
            label: 'Dashboard',
            path: '/app/demo/apps/file-manager',
            icon: <IconLayoutDashboard size={16} />,
          },
          {
            id: 'fm-my-files',
            label: 'My Files',
            path: '/app/demo/apps/file-manager/my-files',
            icon: <IconFolder size={16} />,
            countSelector: () => 128,
          },
          {
            id: 'fm-shared',
            label: 'Shared',
            path: '/app/demo/apps/file-manager/shared',
            icon: <IconUsers size={16} />,
            countSelector: () => 45,
          },
          {
            id: 'fm-trash',
            label: 'Trash',
            path: '/app/demo/apps/file-manager/trash',
            icon: <IconChecklist size={16} />,
            countSelector: () => 8,
            badgeColor: 'gray',
          },
        ],
      },

      // 9. Analytics
      {
        id: 'analytics',
        label: 'Analytics',
        items: [
          {
            id: 'analytics-dashboard',
            label: 'Dashboard',
            path: '/app/demo/apps/analytics',
            icon: <IconLayoutDashboard size={16} />,
          },
          {
            id: 'analytics-reports',
            label: 'Reports',
            path: '/app/demo/apps/analytics/reports',
            icon: <IconListDetails size={16} />,
            countSelector: () => 15,
          },
          {
            id: 'analytics-audience',
            label: 'Audience',
            path: '/app/demo/apps/analytics/audience',
            icon: <IconUsers size={16} />,
            countSelector: () => '24.5K',
          },
          {
            id: 'analytics-exports',
            label: 'Exports',
            path: '/app/demo/apps/analytics/exports',
            icon: <IconDatabase size={16} />,
            countSelector: () => 3,
            badgeColor: 'cyan',
          },
        ],
      },

      // 10. Chat
      {
        id: 'chat',
        label: 'Chat',
        items: [
          {
            id: 'chat-messages',
            label: 'Messages',
            path: '/app/demo/apps/chat',
            icon: <IconMessage size={16} />,
            countSelector: () => 12,
            badgeColor: 'red',
          },
          {
            id: 'chat-contacts',
            label: 'Contacts',
            path: '/app/demo/apps/chat/contacts',
            icon: <IconUsers size={16} />,
            countSelector: () => 86,
          },
          {
            id: 'chat-groups',
            label: 'Groups',
            path: '/app/demo/apps/chat/groups',
            icon: <IconUsers size={16} />,
            countSelector: () => 8,
          },
          {
            id: 'chat-settings',
            label: 'Settings',
            path: '/app/demo/apps/chat/settings',
            icon: <IconSettings size={16} />,
          },
        ],
      },

      // 11. Calendar App
      {
        id: 'calendar',
        label: 'Calendar',
        items: [
          {
            id: 'calendar-main',
            label: 'Calendar',
            path: '/app/demo/apps/calendar',
            icon: <IconCalendar size={16} />,
          },
          {
            id: 'calendar-events',
            label: 'Events',
            path: '/app/demo/apps/calendar/events',
            icon: <IconListDetails size={16} />,
            countSelector: () => 28,
          },
          {
            id: 'calendar-schedule',
            label: 'Schedule',
            path: '/app/demo/apps/calendar/schedule',
            icon: <IconChecklist size={16} />,
            countSelector: () => 5,
            badgeColor: 'violet',
          },
          {
            id: 'calendar-rooms',
            label: 'Meeting Rooms',
            path: '/app/demo/apps/calendar/rooms',
            icon: <IconFolders size={16} />,
            countSelector: () => 6,
          },
        ],
      },

      // 12. Invoicing
      {
        id: 'invoicing',
        label: 'Invoicing',
        items: [
          {
            id: 'invoicing-dashboard',
            label: 'Dashboard',
            path: '/app/demo/apps/invoicing',
            icon: <IconLayoutDashboard size={16} />,
          },
          {
            id: 'invoicing-invoices',
            label: 'Invoices',
            path: '/app/demo/apps/invoicing/invoices',
            icon: <IconReceipt size={16} />,
            countSelector: () => 42,
          },
          {
            id: 'invoicing-payments',
            label: 'Payments',
            path: '/app/demo/apps/invoicing/payments',
            icon: <IconShoppingCart size={16} />,
            countSelector: () => 8,
            badgeColor: 'yellow',
          },
          {
            id: 'invoicing-clients',
            label: 'Clients',
            path: '/app/demo/apps/invoicing/clients',
            icon: <IconUsers size={16} />,
            countSelector: () => 67,
          },
        ],
      },

      // 13. Helpdesk
      {
        id: 'helpdesk',
        label: 'Helpdesk',
        items: [
          {
            id: 'helpdesk-dashboard',
            label: 'Dashboard',
            path: '/app/demo/apps/helpdesk',
            icon: <IconLayoutDashboard size={16} />,
          },
          {
            id: 'helpdesk-tickets',
            label: 'Tickets',
            path: '/app/demo/apps/helpdesk/tickets',
            icon: <IconListDetails size={16} />,
            countSelector: () => 23,
            badgeColor: 'red',
          },
          {
            id: 'helpdesk-knowledge',
            label: 'Knowledge Base',
            path: '/app/demo/apps/helpdesk/knowledge',
            icon: <IconDatabase size={16} />,
            countSelector: () => 156,
          },
          {
            id: 'helpdesk-reports',
            label: 'Reports',
            path: '/app/demo/apps/helpdesk/reports',
            icon: <IconChartBar size={16} />,
          },
        ],
      },

      // 14. UI Components
      {
        id: 'ui-components',
        label: 'UI Components',
        items: [
          {
            id: 'ui-core',
            label: 'Core',
            path: '/app/demo/ui/core',
            icon: <IconSquare size={16} />,
          },
          {
            id: 'ui-inputs',
            label: 'Inputs',
            path: '/app/demo/ui/inputs',
            icon: <IconForms size={16} />,
          },
          {
            id: 'ui-data-display',
            label: 'Data Display',
            path: '/app/demo/ui/data-display',
            icon: <IconDatabase size={16} />,
          },
          {
            id: 'ui-feedback',
            label: 'Feedback',
            path: '/app/demo/ui/feedback',
            icon: <IconSparkles size={16} />,
          },
          {
            id: 'ui-alerts',
            label: 'Alerts',
            path: '/app/demo/ui/alerts',
            icon: <IconBell size={16} />,
          },
          {
            id: 'ui-navigation',
            label: 'Navigation',
            path: '/app/demo/ui/navigation',
            icon: <IconListCheck size={16} />,
          },
          {
            id: 'ui-overlays',
            label: 'Overlays',
            path: '/app/demo/ui/overlays',
            icon: <IconClick size={16} />,
          },
          {
            id: 'ui-layout',
            label: 'Layout',
            path: '/app/demo/ui/layout',
            icon: <IconTable size={16} />,
          },
        ],
      },

      // 15. Advanced Components
      {
        id: 'advanced',
        label: 'Advanced',
        items: [
          {
            id: 'advanced-calendar',
            label: 'Calendar',
            path: '/app/demo/advanced/calendar',
            icon: <IconCalendar size={16} />,
          },
          {
            id: 'advanced-workflow',
            label: 'Workflow',
            path: '/app/demo/advanced/workflow',
            icon: <IconChecklist size={16} />,
          },
          {
            id: 'advanced-editor',
            label: 'Editor',
            path: '/app/demo/advanced/editor',
            icon: <IconEdit size={16} />,
          },
          {
            id: 'advanced-data-tables',
            label: 'Data Tables',
            path: '/app/demo/advanced/data-tables',
            icon: <IconTable size={16} />,
          },
          {
            id: 'advanced-form-builder',
            label: 'Form Builder',
            path: '/app/demo/advanced/form-builder',
            icon: <IconForms size={16} />,
            countSelector: () => 'New',
            badgeColor: 'green',
          },
        ],
      },

      // 16. Widgets
      {
        id: 'widgets',
        label: 'Widgets',
        items: [
          {
            id: 'widgets-kpi',
            label: 'KPI Widgets',
            path: '/app/demo/advanced/widgets/kpi',
            icon: <IconChartBar size={16} />,
          },
          {
            id: 'widgets-line-chart',
            label: 'Line Charts',
            path: '/app/demo/advanced/widgets/line-chart',
            icon: <IconChartLine size={16} />,
          },
          {
            id: 'widgets-bar-chart',
            label: 'Bar Charts',
            path: '/app/demo/advanced/widgets/bar-chart',
            icon: <IconChartBar size={16} />,
          },
          {
            id: 'widgets-pie-chart',
            label: 'Pie Charts',
            path: '/app/demo/advanced/widgets/pie-chart',
            icon: <IconChartPie size={16} />,
          },
          {
            id: 'widgets-area-chart',
            label: 'Area Charts',
            path: '/app/demo/advanced/widgets/area-chart',
            icon: <IconChartArea size={16} />,
          },
          {
            id: 'widgets-data-table',
            label: 'Data Tables',
            path: '/app/demo/advanced/widgets/data-table',
            icon: <IconTableOptions size={16} />,
          },
          {
            id: 'widgets-gauges',
            label: 'Gauges',
            path: '/app/demo/advanced/widgets/gauges',
            icon: <IconGauge size={16} />,
          },
          {
            id: 'widgets-heatmaps',
            label: 'Heatmaps',
            path: '/app/demo/advanced/widgets/heatmaps',
            icon: <IconChartDots size={16} />,
          },
          {
            id: 'widgets-alerts',
            label: 'Alerts',
            path: '/app/demo/advanced/widgets/alerts',
            icon: <IconAlertCircle size={16} />,
          },
          {
            id: 'widgets-ai-insights',
            label: 'AI Insights',
            path: '/app/demo/advanced/widgets/ai-insights',
            icon: <IconBrain size={16} />,
          },
          {
            id: 'widgets-all',
            label: 'All Charts',
            path: '/app/demo/advanced/widgets',
            icon: <IconChartBar size={16} />,
          },
        ],
      },

      // 17. Animations
      {
        id: 'animations',
        label: 'Animations',
        items: [
          {
            id: 'animations-transitions',
            label: 'Transitions',
            path: '/app/demo/animations/transitions',
            icon: <IconSparkles size={16} />,
          },
          {
            id: 'animations-interactive',
            label: 'Interactive',
            path: '/app/demo/animations/interactive',
            icon: <IconClick size={16} />,
          },
          {
            id: 'animations-scroll',
            label: 'Scroll Effects',
            path: '/app/demo/animations/scroll',
            icon: <IconArrowsMove size={16} />,
          },
        ],
      },

      // 18. Domain Examples
      {
        id: 'domain-examples',
        label: 'Domain Examples',
        items: [
          {
            id: 'domains-projects',
            label: 'Projects',
            path: '/app/demo/domains/projects',
            icon: <IconFolders size={16} />,
          },
          {
            id: 'domains-tasks',
            label: 'Tasks',
            path: '/app/demo/domains/tasks',
            icon: <IconChecklist size={16} />,
          },
          {
            id: 'domains-members',
            label: 'Members',
            path: '/app/demo/domains/members',
            icon: <IconUsers size={16} />,
          },
        ],
      },

      // 19. Settings & Theming
      {
        id: 'settings-theming',
        label: 'Settings & Theming',
        items: [
          {
            id: 'settings-colors',
            label: 'Color Schemes',
            path: '/app/demo/settings/colors',
            icon: <IconPalette size={16} />,
          },
          {
            id: 'settings-theme',
            label: 'Theme Mode',
            path: '/app/demo/settings/theme',
            icon: <IconMoon size={16} />,
          },
          {
            id: 'settings-typography',
            label: 'Typography',
            path: '/app/demo/settings/typography',
            icon: <IconTypography size={16} />,
          },
          {
            id: 'settings-translations',
            label: 'Translations',
            path: '/app/demo/settings/translations',
            icon: <IconLanguage size={16} />,
          },
          {
            id: 'settings-accessibility',
            label: 'Accessibility',
            path: '/app/demo/settings/accessibility',
            icon: <IconAccessible size={16} />,
          },
        ],
      },

      // 20. Auth Pages
      {
        id: 'auth-pages',
        label: 'Auth Pages',
        items: [
          {
            id: 'auth-overview',
            label: 'Overview',
            path: '/app/demo/auth-pages',
            icon: <IconLayoutGrid size={16} />,
          },
          {
            id: 'auth-login',
            label: 'Login Templates',
            path: '/app/demo/auth-pages/login',
            icon: <IconLogin size={16} />,
            countSelector: () => 17,
          },
          {
            id: 'auth-register',
            label: 'Register Templates',
            path: '/app/demo/auth-pages/register',
            icon: <IconUserPlus size={16} />,
            countSelector: () => 17,
          },
          {
            id: 'auth-forgot-password',
            label: 'Forgot Password',
            path: '/app/demo/auth-pages/forgot-password',
            icon: <IconKey size={16} />,
            countSelector: () => 17,
          },
          {
            id: 'auth-reset-password',
            label: 'Reset Password',
            path: '/app/demo/auth-pages/reset-password',
            icon: <IconLockCheck size={16} />,
            countSelector: () => 17,
          },
          {
            id: 'auth-otp',
            label: 'OTP / 2FA',
            path: '/app/demo/auth-pages/otp',
            icon: <IconDeviceMobile size={16} />,
            countSelector: () => 17,
          },
          {
            id: 'auth-other',
            label: 'Other Templates',
            path: '/app/demo/auth-pages/other',
            icon: <IconDots size={16} />,
            countSelector: () => 5,
          },
        ],
      },

      // 21. Legacy Pages
      {
        id: 'legacy',
        label: 'Legacy',
        items: [
          {
            id: 'legacy-forms',
            label: 'Forms (Legacy)',
            path: '/app/demo/forms',
            icon: <IconForms size={16} />,
          },
          {
            id: 'legacy-tables',
            label: 'Tables (Legacy)',
            path: '/app/demo/tables',
            icon: <IconTable size={16} />,
          },
        ],
      },
    ];

    return {
      domainId: this.domainId,
      sections,
      metadata: {
        version: '1.0.0',
        lastUpdated: Date.now(),
        description: 'Demo domain menu with 21 sections and 150+ pages',
      },
    };
  }
}

export const demoMenuProvider = new DemoMenuProvider();
